import { useCallback, useEffect, useState } from "react";
import InformationBox from "../../components/information-box/information-box";
import Table, { ITableHeader, ITableRow } from "../../components/table/table";
import { applicantHttpService, caseHttpService } from "@myinnsworth/httpServices";
import { useOutletContext, useParams } from "react-router";
import ICase from "@myinnsworth/models/case/ICase";
import { format } from "date-fns";
import ILossResults from "@myinnsworth/models/lossResults/ILossResults";
import { CaseLayoutContext } from "containers/layouts/case/case";
import { NumericFormat } from 'react-number-format';
import { useAppStore } from "@myinnsworth/hooks/appStoreHook";

const headers: Array<ITableHeader> = [
    { name: 'Loss Method' },
    { name: 'Total loss', width: '180px' }
];

export default function LossReport() {
    const params = useParams();
    const appStore = useAppStore();
    const outletContext = useOutletContext<CaseLayoutContext>();
    const [currentCase, setCurrentCase] = useState<ICase | undefined>();
    const [lossResults, setLossResults] = useState<ILossResults | undefined>();
    const [tableRows, setTableRows] = useState<Array<ITableRow>>([]);
    const [lastSlug, setLastSlug] = useState<string | undefined>();

    const fetchLossResults = useCallback(async () => {
        let results: ILossResults | undefined = undefined;

        try {
            const isCaseView = params.slug && !params.institutionId && !params.fundId;
            const isInstitutionView = params.slug && params.institutionId && !params.fundId;
            const isFundView = params.slug && params.institutionId && params.fundId;
            
            if(isCaseView) {
                if(lastSlug !== params.slug) {
                    setLastSlug(lastSlug);
                    results = await caseHttpService.fetchLossResults(params.slug!);
                }
            } else if(isInstitutionView) {
                results = await applicantHttpService.fetchInstitutionLossResults(params.slug!, parseInt(params.institutionId!));
            } else if(isFundView) {
                results = await applicantHttpService.fetchFundLossResults(params.slug!, parseInt(params.institutionId!), parseInt(params.fundId!));
            }
        } catch {
            results = undefined;
        } finally {
            setLossResults(results);
        }
    }, [params.slug, lastSlug, params.institutionId, params.fundId]);

    useEffect(() => {
        if(lossResults && currentCase) {
            setTableRows(lossResults.losses.map(result => {
                let total = result.lossAmount;
                if(outletContext.totalType) {
                    switch(outletContext.totalType) {
                        case 'all': {
                            total = result.lossAmount;
                            break;
                        }
                        case 'active': {
                            total = result.activeLossAmount;
                            break;
                        }
                        case 'signed': {
                            total = result.signedLossAmount;
                            break;
                        }
                    }
                }
                return {
                    cells: [result.details, <NumericFormat value={total.toFixed(2)} thousandSeparator displayType="text" prefix={currentCase.currency.symbol} /> ]
                };
            }));
        }
    }, [lossResults, currentCase, outletContext.totalType]);

    useEffect(() => {
        setCurrentCase(appStore.findCase(params.slug!));
    }, [appStore, params.slug]);

    useEffect(() => {
        fetchLossResults();
    }, [fetchLossResults]);

    return (
        <>
            <div className="spacing-m"></div>
            <div className="grid col-2 loss-report-container">
                {currentCase && (<>
                    <InformationBox title="Exchange" items={
                        [
                            { label: 'Exchange', data: currentCase?.exchangeName },
                            { label: 'Exchange Symbol', data: currentCase?.exchangeSymbolCode },
                            { label: 'Currency', data: currentCase?.currency.code }
                        ]
                    } />
                    <InformationBox title="Date" items={
                        [
                            { label: 'Start date', data: format(currentCase?.activityStart!, 'dd/MM/yyyy') },
                            { label: 'End date', data: format(currentCase?.activityEnd!, 'dd/MM/yyyy') }
                        ]
                    } />
                </>)}
            </div>

            <div className="flex">
                <Table headers={headers} rows={tableRows} allowPaging={false} nameIndex={0} />
            </div>
        </>
    )
}